import type {
  AdminArchivedCompanyDocumentsResponse,
  AdminCompanyDocumentsResponse,
  BulkRestoreItem,
} from '@/api/(admin)/documents/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Pure RPC over the admin company-document endpoints.
 *
 * The two lists are separate routes rather than one route with an `archived`
 * flag, so the hook picks between them; paging, sort and filter live there too
 * and arrive here as a built query string.
 */

/**
 * Every document that is not archived anywhere. Migrated to
 * `/api/admin/company-documents`, so it takes `API_ROOT_URL`; `apiFetch`'s
 * default base would put `/api-service` in front of the path.
 */
export async function fetchAdminCompanyDocuments(
  qs: string,
): Promise<AdminCompanyDocumentsResponse> {
  return apiFetch<AdminCompanyDocumentsResponse>(
    `/api/admin/company-documents?${qs}`,
    undefined,
    API_ROOT_URL,
  );
}

/**
 * The archive. One row per `(document, archiving company)` pair, so a document
 * archived by two companies appears twice — see `archivedByCompanyId`.
 * Migrated to `/api/admin/company-documents/archived`, so it takes
 * `API_ROOT_URL`.
 */
export async function fetchAdminArchivedCompanyDocuments(
  qs: string,
): Promise<AdminArchivedCompanyDocumentsResponse> {
  return apiFetch<AdminArchivedCompanyDocumentsResponse>(
    `/api/admin/company-documents/archived?${qs}`,
    undefined,
    API_ROOT_URL,
  );
}

/**
 * Takes documents out of the archive. A `PATCH`, matching the route. Migrated
 * to `/api/admin/company-documents/bulk-restore`, so it takes `API_ROOT_URL`.
 * The response names the pairs restored; nothing here reads it.
 */
export async function bulkRestoreCompanyDocuments(
  items: BulkRestoreItem[],
): Promise<void> {
  await apiFetch<unknown>(
    '/api/admin/company-documents/bulk-restore',
    { method: 'PATCH', body: JSON.stringify(items) },
    API_ROOT_URL,
  );
}
